import { forwardRef, useId } from "react";
import { formatAuMobile } from "@/lib/auPhone";

interface AuMobileInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  /** Tailwind classes for the inner input, matching the form's other fields. */
  inputClassName: string;
  ariaLabel?: string;
  placeholder?: string;
  /** Visible floating label text. */
  label?: string;
  /** id of the element describing the error, for aria-describedby. */
  errorId?: string;
  /** Show the subtle valid tick. */
  isValid?: boolean;
}

/**
 * Australian mobile input: fixed, non-editable +61 prefix with live formatting.
 * `value` is stored as the national portion (e.g. "412 345 678").
 */
const AuMobileInput = forwardRef<HTMLInputElement, AuMobileInputProps>(
  (
    {
      id,
      value,
      onChange,
      onBlur,
      hasError,
      inputClassName,
      ariaLabel,
      placeholder = "412 345 678",
      label,
      errorId,
      isValid,
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id ?? `au-mobile-${autoId}`;
    return (
      <div
        className={`flex items-stretch overflow-hidden rounded-xl border-2 bg-white transition-all duration-200 ease-out focus-within:border-[#FDC00E] focus-within:shadow-[0_0_0_4px_rgba(253,192,14,0.18)] ${
          hasError
            ? "border-red-500 focus-within:border-red-500 focus-within:shadow-[0_0_0_4px_rgba(239,68,68,0.18)]"
            : isValid
              ? "border-success/60"
              : "border-[#1a1a1a]/10"
        }`}
      >
        <span
          aria-hidden="true"
          className="flex items-center gap-2 pl-4 pr-3 pt-5 pb-2 font-bold text-[#1a1a1a]/70 select-none border-r border-[#1a1a1a]/10 bg-[#1a1a1a]/[0.03]"
        >
          +61
        </span>
        <div className="relative flex-1 min-w-0">
          {label && (
            <label
              htmlFor={inputId}
              className="pointer-events-none absolute left-4 top-1.5 text-xs font-semibold text-[#1a1a1a]/55"
            >
              {label}
            </label>
          )}
          <input
            ref={ref}
            id={inputId}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            aria-label={label ? undefined : (ariaLabel ?? "Australian mobile number")}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError && errorId ? errorId : undefined}
            placeholder={placeholder}
            value={value}
            onBlur={onBlur}
            onChange={(e) => onChange(formatAuMobile(e.target.value))}
            className={`${inputClassName} border-0 rounded-none focus:border-0 focus:ring-0 focus:outline-none w-full min-w-0 ${
              label ? "pt-6 pb-2" : ""
            }`}
          />
        </div>
      </div>
    );
  },
);
AuMobileInput.displayName = "AuMobileInput";

export default AuMobileInput;
